/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import type { IconName } from '@/components/Icon';
import { ENTRY_SOURCE_PARAM } from '@/lib/entrySource';
import { encodeSearch } from '@/lib/searchString';

export interface NavItem {
  key: string;
  label: string;
  to: string;
  icon: IconName;
  end?: boolean;
}

/** Primary rail destinations (top group). Manufacturing workflow entry points. */
export const navTop: NavItem[] = [
  { key: 'home', label: 'Home', to: '/', icon: 'home', end: true },
  { key: 'repairOverview', label: 'Repair overview', to: '/repair-overview', icon: 'history' },
  { key: 'online', label: 'On-Line', to: '/online', icon: 'directions_car' },
  { key: 'offline', label: 'Off-Line', to: '/offline', icon: 'lift' },
  { key: 'gsip', label: 'GSIP', to: '/gsip', icon: 'fact_check' },
  { key: 'dtc', label: 'Diagnostic Search', to: '/dtc', icon: 'warning' },
  { key: 'schematics', label: 'Schematics', to: '/schematic-explorer', icon: 'schema' },
  { key: 'mecl', label: 'MECL', to: '/mecl', icon: 'list' },
  {
    key: 'engineering',
    label: 'Engineering Diagnostics',
    to: '/engineering-diagnostics',
    icon: 'bolt',
  },
  { key: 'resources', label: 'Resource links', to: '/resource-links', icon: 'menu_book' },
];

/** Secondary rail destinations (bottom group). */
export const navBottom: NavItem[] = [
  { key: 'analytics', label: 'Analytics', to: '/analytics', icon: 'analytics' },
  { key: 'admin', label: 'Admin tools', to: '/admin', icon: 'admin_panel_settings' },
];

/** URL-driven tools whose last URL is remembered across navigation. */
export const REMEMBERED_ROUTES = ['/schematic-explorer', '/mecl'];

/** Base path for a remembered tool, or null. */
export function rememberedBase(pathname: string): string | null {
  return REMEMBERED_ROUTES.find((b) => pathname === b || pathname.startsWith(`${b}/`)) ?? null;
}

/**
 * Query keys that identify the current vehicle and are shared across the MECL and Schematic
 * Explorer tools (tool-specific keys like `tab`, `dtcs` or a diagram path are intentionally left
 * out). Both pages resolve their vehicle from these params.
 */
const VEHICLE_QUERY_KEYS = [
  'program',
  'programCode',
  'year',
  'make',
  'model',
  'release',
  'vin',
  'system',
  'modelDesignator',
  // Which search opened the vehicle, so the sibling tool's header names the same thing this one
  // does. Dropping it left the sibling to guess from the identity params and guess differently.
  ENTRY_SOURCE_PARAM,
] as const;

/**
 * Build the shared vehicle query (no leading `?`) from a tool URL's search params, so the same
 * vehicle can be mirrored onto the sibling tool. Returns an empty string when no vehicle keys are
 * present. Key order is fixed for a stable, comparable string.
 */
export function vehicleQuery(params: URLSearchParams): string {
  const out = new URLSearchParams();
  for (const key of VEHICLE_QUERY_KEYS) {
    const value = params.get(key);
    if (value) out.set(key, value);
  }
  return encodeSearch(out);
}
